import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import RawHtml from 'common/RawHtml';
import fieldActions from 'core/fields/actions';

import checkboxStyles from 'styles/checkbox';
import dataActions from 'core/data/actions';
import dialogActions from 'core/dialogs/actions';

const styles = (theme) => {
  const { spacing, typography } = theme;
  return {
    checkbox: {
      ...checkboxStyles(theme),
      left: 10,
      position: 'absolute',
      top: 12,
    },
    checkmark: {
      padding: [0, 0, 0, 20 + spacing.unit * 2],
    },
    label: {
      ...typography.h3,
      display: 'inline-block',
      marginBottom: spacing.unit * 2,
      paddingRight: 70,
      position: 'relative',
    },
  };
};

const Checkmark = ({
  changeData,
  checked,
  classes,
  fieldChosen,
  id,
  isDisabled,
  label,
  name,
  onClick,
  stopSelection,
  toggleDialog,
  validate,
  warningMessage,
}) => {
  const checkmarkRef = useRef(null);
  const onChange = () => {
    if (stopSelection) {
      toggleDialog('warning', { message: warningMessage, title: 'Offer limit' });
      return;
    }
    onClick(!checked);
    changeData(id, !checked);
    validate(fieldChosen, true);

    const { id: refId } = checkmarkRef.current;
    setTimeout(() => {
      const el = document.querySelector(`#${refId}`)?.nextElementSibling;
      return el ? el?.focus?.() : undefined;
    });
  };

  return (
    <div className={classnames('checkbox form-group square', classes.checkmark)}>
      <input
        type="checkbox"
        id={id}
        name={id}
        data-offer-id={name}
        disabled={isDisabled}
        onChange={onChange}
        checked={checked}
        className={classes.checkbox}
        ref={checkmarkRef}
      />{' '}
      <RawHtml
        type="label"
        id={`label-${id}`}
        htmlFor={id}
        className={classnames({ disabled: isDisabled }, classes.label)}
      >
        {label || ' '}
      </RawHtml>
    </div>
  );
};

Checkmark.propTypes = {
  changeData: PropTypes.func,
  checked: PropTypes.bool.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  fieldChosen: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  stopSelection: PropTypes.bool,
  toggleDialog: PropTypes.func,
  validate: PropTypes.func,
  warningMessage: PropTypes.string,
};

Checkmark.defaultProps = {
  changeData: () => {},
  onClick: () => {},
  stopSelection: false,
  toggleDialog: () => {},
  validate: () => {},
  warningMessage: '',
};

export const CheckmarkBase = withStyles(styles)(Checkmark);

const mapStateToProps = ({ bundles, data, offers }, { name }) => {
  const { requestBundle } = bundles;
  const isPrimaryOffer = requestBundle?.offers?.includes(name) || false;
  const offer = offers[name] || {};
  const [, bundle] = Object.entries(bundles).find(([, b]) => b.offers?.includes(name)) || [
    undefined,
    {},
  ];
  const offersInBundlePicked = bundle.offers?.filter((o) => data[offers[o]?.fieldChosen]).length;

  return {
    checked: data[offer.fieldChosen],
    fieldChosen: offer.fieldChosen,
    id: offer.fieldChosen,
    isDisabled: isPrimaryOffer,
    label: offer.label,
    stopSelection:
      !!bundle.subSetLimit &&
      !data[offer.fieldChosen] &&
      offersInBundlePicked === bundle.subSetLimit,
    warningMessage: `You can select up to ${bundle.subSetLimit} offer${
      bundle.subSetLimit > 1 ? 's' : ''
    }.`,
  };
};

const mapDispatchToProps = {
  changeData: dataActions.changeData,
  toggleDialog: dialogActions.open,
  validate: fieldActions.validate,
};

export default connect(mapStateToProps, mapDispatchToProps)(CheckmarkBase);
